import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// Legend and FiX are only used by the commented-out coordinator bar chart.
/* eslint-disable no-unused-vars */
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, ComposedChart,
} from 'recharts';
import { FiCalendar, FiRefreshCw, FiGlobe, FiChevronDown, FiCheck, FiX, FiBarChart2, FiAward, FiTrendingUp, FiPercent, FiUser } from 'react-icons/fi';
/* eslint-enable no-unused-vars */
import { PiNotepadBold } from 'react-icons/pi';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiRequest';
import { API_URLS } from '../config';
import './CoordinatorDashboard.css';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { TbReportAnalytics } from 'react-icons/tb';

const WIN_RATE_COLOR = (rate) => {
  if (rate >= 70) return { bg: '#e6f4ec', color: '#1a7a3c' };
  if (rate >= 40) return { bg: '#fff7e6', color: '#b45309' };
  return { bg: '#fdecea', color: '#b91c1c' };
};

const StatCard = ({ label, sub, children }) => (
  <div className="cd-stat-card">
    <div className="cd-stat-label">{label}</div>
    <div className="cd-stat-value">{children}</div>
    <div className="cd-stat-sub">{sub}</div>
  </div>
);

const CoordinatorDashboard = () => {
  useAuth();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const [from, setFrom]               = useState('');
  const [to, setTo]                   = useState('');
  const [calOpen, setCalOpen]         = useState(false);
  const [pendingFrom, setPendingFrom] = useState('');
  const [pendingTo, setPendingTo]     = useState('');

  const [companies, setCompanies]               = useState([]);
  const [selectedCompany, setSelectedCompany]   = useState(null);
  const [companyOpen, setCompanyOpen]           = useState(false);

  const [coordFilter, setCoordFilter]     = useState(null); // { id, name } — backend filter
  const [coordFilterOpen, setCoordFilterOpen] = useState(false);
  const [coordOptions, setCoordOptions]   = useState([]); // stable list for dropdown

  const [selectedCoord, setSelectedCoord] = useState(null);

  const [showAllClients,      setShowAllClients]      = useState(false);
  const [showAllCountries,    setShowAllCountries]    = useState(false);
  const [showAllRevCountries, setShowAllRevCountries] = useState(false);

  const nowDate = new Date();
  const currentYM = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentYM);
  const [monthOpen, setMonthOpen]         = useState(false);

  const [offerMonth]                       = useState(currentYM);

  // ── Timeline slicer ───────────────────────────────────────
  // Each item: { label, fromYM (unique key), toYM, fromDate, toDate, groupKey, groupLabel }
  const MONTH_ABR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dim = (y, m) => new Date(y, m, 0).getDate(); // days in month

  const slicerAllMonths = useMemo(() => {
    const arr = [];
    for (let y = nowDate.getFullYear() - 3; y <= nowDate.getFullYear(); y++) {
      for (let m = 1; m <= 12; m++) {
        const mm = String(m).padStart(2, '0');
        const ym = `${y}-${mm}`;
        arr.push({ label: MONTH_ABR[m - 1], fromYM: ym, toYM: ym,
          fromDate: `${ym}-01`, toDate: `${ym}-${dim(y, m)}`,
          groupKey: String(y), groupLabel: String(y) });
      }
    }
    return arr;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const slicerAllQuarters = useMemo(() => {
    const arr = [];
    for (let y = nowDate.getFullYear() - 3; y <= nowDate.getFullYear(); y++) {
      for (let q = 1; q <= 4; q++) {
        const sm = (q - 1) * 3 + 1;
        const em = sm + 2;
        const smm = String(sm).padStart(2, '0');
        const emm = String(em).padStart(2, '0');
        arr.push({ label: `Q${q}`, fromYM: `${y}-${smm}`, toYM: `${y}-${emm}`,
          fromDate: `${y}-${smm}-01`, toDate: `${y}-${emm}-${dim(y, em)}`,
          groupKey: String(y), groupLabel: String(y) });
      }
    }
    return arr;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const slicerAllYears = useMemo(() => {
    const arr = [];
    for (let y = nowDate.getFullYear() - 3; y <= nowDate.getFullYear(); y++) {
      arr.push({ label: String(y), fromYM: `${y}-01`, toYM: `${y}-12`,
        fromDate: `${y}-01-01`, toDate: `${y}-12-31`,
        groupKey: String(y), groupLabel: String(y) });
    }
    return arr;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const slicerAllDays = useMemo(() => {
    const arr = [];
    for (let y = nowDate.getFullYear() - 3; y <= nowDate.getFullYear(); y++) {
      for (let m = 1; m <= 12; m++) {
        const mm = String(m).padStart(2, '0');
        for (let d = 1; d <= dim(y, m); d++) {
          const dd = String(d).padStart(2, '0');
          const full = `${y}-${mm}-${dd}`;
          arr.push({ label: String(d), fromYM: full, toYM: full,
            fromDate: full, toDate: full,
            groupKey: `${y}-${mm}`, groupLabel: `${MONTH_ABR[m - 1]} ${y}` });
        }
      }
    }
    return arr;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [slicerLevel,    setSlicerLevel]    = useState('months'); // 'days'|'months'|'quarters'|'years'
  const [levelDropOpen,  setLevelDropOpen]  = useState(false);
  const slicerAllItems =
    slicerLevel === 'quarters' ? slicerAllQuarters :
    slicerLevel === 'years'    ? slicerAllYears    :
    slicerLevel === 'days'     ? slicerAllDays     : slicerAllMonths;
  const SLICER_VIS    = slicerLevel === 'months' ? 13 : slicerLevel === 'quarters' ? 8 : slicerLevel === 'days' ? 21 : slicerAllYears.length;
  const SLICER_SCROLL = slicerLevel === 'months' ? 6  : slicerLevel === 'quarters' ? 4 : slicerLevel === 'days' ? 7  : 1;
  const [slicerStart,  setSlicerStart]  = useState(() => Math.max(0, slicerAllMonths.length - 13));
  const [slicerDrag,   setSlicerDrag]   = useState(false);
  const [slicerAnchor, setSlicerAnchor] = useState(null);
  const [slicerCursor, setSlicerCursor] = useState(null);

  const monthOptions = useMemo(() => {
    const opts = [];
    // Start from December of the current year, go back 24 months
    for (let i = 0; i < 24; i++) {
      const d = new Date(nowDate.getFullYear(), 11 - i, 1);
      opts.push({
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleString('default', { month: 'long', year: 'numeric' }),
        short: d.toLocaleString('default', { month: 'short',  year: 'numeric' }),
      });
    }
    return opts;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const calRef          = useRef(null);
  const companyRef      = useRef(null);
  const coordFilterRef  = useRef(null);
  const monthRef        = useRef(null);
  const levelDropRef    = useRef(null);

  // ── Fetch companies ───────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('token');
      const res = await apiRequest(API_URLS.COMPANIES, { method: 'GET' }, token);
      if (res?.ok) setCompanies(res.data || []);
    };
    load();
  }, []);

  // ── Fetch dashboard data ──────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setSelectedCoord(null);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to)   params.set('to', to);
    if (selectedCompany) {
      params.set('scope', 'company');
      params.set('companyId', selectedCompany.id || selectedCompany._id);
    } else {
      params.set('scope', 'global');
    }
    if (coordFilter) params.set('coordinatorId', coordFilter.id);
    params.set('viewMonth', selectedMonth);
    params.set('offerMonth', offerMonth);
    const res = await apiRequest(
      `${API_URLS.DASHBOARD}/coordinator?${params.toString()}`,
      { method: 'GET' },
      token
    );
    if (res?.ok) {
      setData(res.data);
      // Keep dropdown list stable — only refresh when viewing all coordinators
      if (!coordFilter) setCoordOptions(res.data.coordinators || []);
    }
    setLoading(false);
  }, [from, to, selectedCompany, coordFilter, selectedMonth, offerMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Close dropdowns on outside click ─────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (calRef.current         && !calRef.current.contains(e.target))         setCalOpen(false);
      if (companyRef.current     && !companyRef.current.contains(e.target))     setCompanyOpen(false);
      if (coordFilterRef.current && !coordFilterRef.current.contains(e.target)) setCoordFilterOpen(false);
      if (monthRef.current       && !monthRef.current.contains(e.target))       setMonthOpen(false);
      if (levelDropRef.current   && !levelDropRef.current.contains(e.target))   setLevelDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const visibleSlicerItems = slicerAllItems.slice(slicerStart, slicerStart + SLICER_VIS);

  // Overlap check against pending selected date range (uses full ISO dates)
  const slicerIsInRange = ({ fromDate, toDate }) => {
    if (!pendingFrom && !pendingTo) return false;
    const lo = [pendingFrom, pendingTo].filter(Boolean).sort()[0];
    const hi = [pendingFrom, pendingTo].filter(Boolean).sort().pop();
    return fromDate <= hi && toDate >= lo;
  };

  // Overlap check for live drag
  const slicerIsDragRange = ({ fromDate, toDate }) => {
    if (!slicerDrag || !slicerAnchor || !slicerCursor) return false;
    const aItem = slicerAllItems.find(i => i.fromYM === slicerAnchor);
    const cItem = slicerAllItems.find(i => i.fromYM === slicerCursor);
    if (!aItem || !cItem) return false;
    const lo = [aItem.fromDate, cItem.fromDate].sort()[0];
    const hi = [aItem.toDate,   cItem.toDate  ].sort().pop();
    return fromDate <= hi && toDate >= lo;
  };

  const slicerApplyRange = (anchorFY, cursorFY) => {
    const a = slicerAllItems.find(i => i.fromYM === anchorFY);
    const b = slicerAllItems.find(i => i.fromYM === cursorFY);
    if (!a || !b) return;
    const fds = [a.fromDate, b.fromDate].sort();
    const tds = [a.toDate,   b.toDate  ].sort();
    setPendingFrom(fds[0]);
    setPendingTo(tds[tds.length - 1]);
  };

  const changeSlicerLevel = (level) => {
    setSlicerLevel(level);
    setLevelDropOpen(false);
    const items =
      level === 'months'   ? slicerAllMonths   :
      level === 'quarters' ? slicerAllQuarters :
      level === 'years'    ? slicerAllYears    : slicerAllDays;
    const vis =
      level === 'months'   ? 13 :
      level === 'quarters' ? 8  :
      level === 'days'     ? 21 : slicerAllYears.length;

    let start = Math.max(0, items.length - vis);
    if (level === 'days') {
      const mm  = String(nowDate.getMonth() + 1).padStart(2, '0');
      const key = `${nowDate.getFullYear()}-${mm}-01`;
      const idx = items.findIndex(i => i.fromYM === key);
      if (idx >= 0) start = idx;
    }
    setSlicerStart(start);
    setSlicerAnchor(null);
    setSlicerCursor(null);
  };

  const onSlicerMouseDown = (fromYM) => {
    setSlicerDrag(true);
    setSlicerAnchor(fromYM);
    setSlicerCursor(fromYM);
    slicerApplyRange(fromYM, fromYM);
  };

  const onSlicerMouseEnter = (fromYM) => {
    if (!slicerDrag) return;
    setSlicerCursor(fromYM);
    slicerApplyRange(slicerAnchor, fromYM);
  };

  const onSlicerMouseUp = () => setSlicerDrag(false);

  const openCal = () => {
    setPendingFrom(from);
    setPendingTo(to);
    setCalOpen(true);
    if (from) {
      const idx = slicerAllItems.findIndex(i => i.fromDate <= from && i.toDate >= from);
      if (idx >= 0) setSlicerStart(Math.max(0, Math.min(slicerAllItems.length - SLICER_VIS, idx)));
    } else {
      setSlicerStart(Math.max(0, slicerAllItems.length - SLICER_VIS));
    }
  };
  const applyDates = () => { setFrom(pendingFrom); setTo(pendingTo); setCalOpen(false); };
  const clearDates = () => { setFrom(''); setTo(''); setPendingFrom(''); setPendingTo(''); setCalOpen(false); };

  const selectCompany = (c) => { setSelectedCompany(c); setCompanyOpen(false); };

  // ── Derived ───────────────────────────────────────────────
  const { stats, coordinators = [], daily = [], offerPriceTrend = [], topClients = [], costByCountry = [], revenueByCountry = [] } = data || {};

  // Feeds the coordinator bar chart, which is currently commented out in the JSX below.
  // When a coordinator is selected show only their data; otherwise show all
  const chartSource = selectedCoord
    ? coordinators.filter(c => c.id === selectedCoord.id)
    : coordinators;

  // eslint-disable-next-line no-unused-vars
  const chartData = chartSource.map(c => ({
    name:         c.name.split(' ')[0],
    fullName:     c.name,
    'In process': c.inProcess,
    Won:          c.won,
    Lost:         c.lost,
  }));

  const fmtDate = (iso) => {
    if (!iso) return '…';
    const [y, m, d] = iso.split('-');
    return `${d}-${m}-${y.slice(2)}`;
  };
  const dateLabel = from || to ? `${fmtDate(from)} → ${fmtDate(to)}` : '';
  const companyLabel = selectedCompany ? (selectedCompany.companyName || 'Company') : 'Global';

  return (
    <div className="cd-page-wrap">
    <div className="cd-page">

      {/* ── Header ── */}
      <div className="cd-header">
        <div>
          <h1 className="cd-title"><TbReportAnalytics size={28} />  Coordinator Dashboard</h1>
          <p className="cd-subtitle">Enquiry performance by coordinator </p>
        </div>

        <div className="cd-controls">

          {/* Coordinator filter */}
          <div className="cd-company-wrap" ref={coordFilterRef}>
            <button
              className={`cd-ctrl-btn ${coordFilter ? 'cd-ctrl-btn--active' : ''}`}
              onClick={() => setCoordFilterOpen(o => !o)}
            >
              <FiUser size={14} />
              {coordFilter
                ? <span>{coordFilter.name.split(' ')[0]}</span>
                : <span>Coordinator</span>
              }
              <FiChevronDown size={12} className={`cd-chevron ${coordFilterOpen ? 'cd-chevron--open' : ''}`} />
            </button>
            {coordFilterOpen && (
              <div className="cd-company-panel">
                <div className="cd-company-panel-title">Filter by Coordinator</div>
                <button
                  className={`cd-company-item ${!coordFilter ? 'cd-company-item--active' : ''}`}
                  onClick={() => { setCoordFilter(null); setCoordFilterOpen(false); }}
                >
                  <FiUser size={13} />
                  <span>All Coordinators</span>
                  {!coordFilter && <FiCheck size={13} className="cd-company-check" />}
                </button>
                {coordOptions.length > 0 && <div className="cd-company-divider" />}
                {coordOptions.map(c => {
                  const active = coordFilter?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      className={`cd-company-item ${active ? 'cd-company-item--active' : ''}`}
                      onClick={() => { setCoordFilter({ id: c.id, name: c.name }); setCoordFilterOpen(false); }}
                    >
                      <span>{c.name}</span>
                      {active && <FiCheck size={13} className="cd-company-check" />}
                    </button>
                  );
                })}
                {coordOptions.length === 0 && (
                  <div className="cd-company-empty">No coordinators found</div>
                )}
              </div>
            )}
          </div>

          {/* Date range */}
          <div className="cd-cal-wrap" ref={calRef}>
            <button
              className={`cd-ctrl-btn ${(from || to) ? 'cd-ctrl-btn--active' : ''}`}
              onClick={openCal}
            >
              <FiCalendar size={14} />
              {dateLabel && <span>{dateLabel}</span>}
            </button>
            {calOpen && (
              <div
                className="cd-slicer-panel"
                onMouseUp={onSlicerMouseUp}
                onMouseLeave={() => setSlicerDrag(false)}
              >
                {/* Header */}
                <div className="cd-slicer-hdr">
                  <span className="cd-slicer-title">TIMELINE</span>
                  <div className="cd-slicer-level-dd" ref={levelDropRef}>
                    <button
                      className="cd-slicer-level-btn"
                      onClick={() => setLevelDropOpen(o => !o)}
                    >
                      {slicerLevel.charAt(0).toUpperCase() + slicerLevel.slice(1)}
                      <span className="cd-slicer-dd-caret">{levelDropOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10}/>}</span>
                    </button>
                    {levelDropOpen && (
                      <div className="cd-slicer-level-menu">
                        {['Days', 'Months', 'Quarters', 'Years'].map(lvl => {
                          const val = lvl.toLowerCase();
                          return (
                            <button
                              key={val}
                              className={`cd-slicer-level-opt${slicerLevel === val ? ' cd-slicer-level-opt--active' : ''}`}
                              onClick={() => changeSlicerLevel(val)}
                            >
                              {lvl}
                              {slicerLevel === val && <span className="cd-slicer-check">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Group labels above cells (year for months/quarters, month+year for days; hidden for years) */}
                {slicerLevel !== 'years' && (
                  <div className="cd-slicer-year-row">
                    <div className="cd-slicer-arrow-spacer" />
                    <div className="cd-slicer-year-track">
                      {(() => {
                        const groups = [];
                        visibleSlicerItems.forEach(({ groupKey, groupLabel }) => {
                          if (!groups.length || groups[groups.length - 1].key !== groupKey) {
                            groups.push({ key: groupKey, label: groupLabel, count: 1 });
                          } else {
                            groups[groups.length - 1].count++;
                          }
                        });
                        return groups.map(g => (
                          <div key={g.key} className="cd-slicer-year-label" style={{ flex: g.count }}>
                            {g.label}
                          </div>
                        ));
                      })()}
                    </div>
                    <div className="cd-slicer-arrow-spacer" />
                  </div>
                )}

                {/* Cells + navigation */}
                <div className="cd-slicer-month-row">
                  <button
                    className="cd-slicer-nav-btn"
                    onClick={() => setSlicerStart(s => Math.max(0, s - SLICER_SCROLL))}
                    disabled={slicerStart === 0}
                  >‹</button>

                  <div className="cd-slicer-cells">
                    {visibleSlicerItems.map((item, i) => {
                      const inRange = slicerIsInRange(item) || slicerIsDragRange(item);
                      const isGrpStart = slicerLevel !== 'years' && i > 0 && visibleSlicerItems[i - 1].groupKey !== item.groupKey;
                      return (
                        <div
                          key={item.fromYM}
                          className={`cd-slicer-cell${inRange ? ' cd-slicer-cell--sel' : ''}${isGrpStart ? ' cd-slicer-cell--yr-start' : ''}`}
                          onMouseDown={() => onSlicerMouseDown(item.fromYM)}
                          onMouseEnter={() => onSlicerMouseEnter(item.fromYM)}
                        >
                          {item.label}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    className="cd-slicer-nav-btn"
                    onClick={() => setSlicerStart(s => Math.min(slicerAllItems.length - SLICER_VIS, s + SLICER_SCROLL))}
                    disabled={slicerStart >= slicerAllItems.length - SLICER_VIS}
                  >›</button>
                </div>

                {/* Selected range display */}
                <div className="cd-slicer-sel-range">
                  {(pendingFrom || pendingTo)
                    ? `${fmtDate(pendingFrom)} → ${fmtDate(pendingTo)}`
                    : <span style={{ color: '#bbb' }}>Click or drag to select a range</span>
                  }
                </div>

                {/* Actions */}
                <div className="cd-cal-actions">
                  <button className="cd-cal-clear" onClick={clearDates}>Clear</button>
                  <button className="cd-cal-apply" onClick={applyDates}>Apply</button>
                </div>
              </div>
            )}
          </div>

          {/* Company filter */}
          <div className="cd-company-wrap" ref={companyRef}>
            <button
              className={`cd-ctrl-btn ${selectedCompany ? 'cd-ctrl-btn--active' : ''}`}
              onClick={() => setCompanyOpen(o => !o)}
            >
              <FiGlobe size={14} />
              <span>{companyLabel}</span>
              <FiChevronDown size={12} className={`cd-chevron ${companyOpen ? 'cd-chevron--open' : ''}`} />
            </button>

            {companyOpen && (
              <div className="cd-company-panel">
                <div className="cd-company-panel-title">Filter by Company</div>
                <button
                  className={`cd-company-item ${!selectedCompany ? 'cd-company-item--active' : ''}`}
                  onClick={() => selectCompany(null)}
                >
                  <FiGlobe size={13} />
                  <span>Global</span>
                  {!selectedCompany && <FiCheck size={13} className="cd-company-check" />}
                </button>
                {companies.length > 0 && <div className="cd-company-divider" />}
                {companies.map(c => {
                  const cId  = c.id || c._id;
                  const selId = selectedCompany?.id || selectedCompany?._id;
                  const active = selId === cId;
                  return (
                    <button
                      key={cId}
                      className={`cd-company-item ${active ? 'cd-company-item--active' : ''}`}
                      onClick={() => selectCompany(c)}
                    >
                      <span>{c.companyName}</span>
                      {active && <FiCheck size={13} className="cd-company-check" />}
                    </button>
                  );
                })}
                {companies.length === 0 && <div className="cd-company-empty">No companies found</div>}
              </div>
            )}
          </div>

          {/* Refresh */}
          <button className="cd-ctrl-btn" onClick={fetchData} title="Refresh">
            <FiRefreshCw size={14} className={loading ? 'cd-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      {loading && !data ? (
        <div className="cd-loading">Loading…</div>
      ) : !data ? (
        <div className="cd-loading">No data available.</div>
      ) : (
        <>
          {/* ── Stat Cards ── */}
          <div className="cd-stats-row">
            <StatCard
              label={<><PiNotepadBold color='#000' size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Total enquiries</>}
              sub={`${stats.totalCoordinators} coordinator${stats.totalCoordinators !== 1 ? 's' : ''}`}
            >
              <span className="cd-stat-big">{stats.totalEnquiries}</span>
            </StatCard>

            <StatCard
              label={<><FiBarChart2 color='#000' size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Highest volume</>}
              sub={stats.highestVolume ? `${stats.highestVolume.count} enquiries` : '—'}
            >
              <span className="cd-stat-name">{stats.highestVolume?.name || '—'}</span>
            </StatCard>

            <StatCard
              label={<><FiAward color='#000' size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Best win rate</>}
              sub={stats.bestWinRate ? `${stats.bestWinRate.rate}% (${stats.bestWinRate.closed} closed)` : '—'}
            >
              <span className="cd-stat-name cd-green">{stats.bestWinRate?.name || '—'}</span>
            </StatCard>

            <StatCard
              label={<><FiTrendingUp color='#000' size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Most wins</>}
              sub={stats.mostWins ? `${stats.mostWins.wins} won · ${stats.mostWins.rate}% rate` : '—'}
            >
              <span className="cd-stat-name">{stats.mostWins?.name || '—'}</span>
            </StatCard>

            <StatCard
              label={<><FiPercent color='#000' size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Overall win rate</>}
              sub={`${stats.overallWinRate.won} won · ${stats.overallWinRate.total} total`}
            >
              <span className="cd-stat-pct cd-green">{stats.overallWinRate.rate}%</span>
            </StatCard>
          </div>

          {/* ── Performance Table + Monthly Win Rate ── */}
          <div className="cd-two-col">

            {/* Left: coordinator table */}
            <div className="cd-section">
              <div className="cd-section-title">COORDINATOR PERFORMANCE SUMMARY</div>

              {coordinators.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '24px', fontSize: 13 }}>
                  No coordinator data found
                </div>
              ) : (
                <div className="cd-coord-list">
                  <div className="cd-coord-header">
                    <span>COORDINATOR</span>
                    <span>TOTAL</span>
                    <span>IN PROC</span>
                    <span>WON</span>
                    <span>LOST</span>
                    <span>WIN RATE</span>
                  </div>

                  {coordinators.map((c) => {
                    const { bg, color } = WIN_RATE_COLOR(c.winRate);
                    const isSelected = selectedCoord?.id === c.id;
                    return (
                      <div
                        key={c.id}
                        className={`cd-coord-row ${isSelected ? 'cd-coord-row--selected' : ''}`}
                        onClick={() => setSelectedCoord(isSelected ? null : c)}
                        title={isSelected ? 'Click to show all' : 'Click to view individual chart'}
                      >
                        <span className="cd-coord-name">{c.name}</span>
                        <span className="cd-coord-val">{c.total}</span>
                        <span className="cd-coord-val cd-inproc">{c.inProcess}</span>
                        <span className="cd-coord-val cd-won">{c.won}</span>
                        <span className="cd-coord-val cd-lost">{c.lost}</span>
                        <span className="cd-badge" style={{ background: bg, color }}>{c.winRate}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: daily win rate trend */}
            <div className="cd-section cd-trend-card" style={{ marginBottom : 0}}>

              {/* Header: title + month picker */}
              <div className="cd-trend-header">
                <div>
                  <div className="cd-section-title" style={{ marginBottom: 2 }}>WIN TREND</div>
                  <div className="cd-trend-sub">Daily won inspections for selected month</div>
                </div>
                {!from && !to && (
                  <div className="cd-month-wrap" ref={monthRef}>
                    <button
                      className={`cd-ctrl-btn ${monthOpen ? 'cd-ctrl-btn--active' : ''}`}
                      onClick={() => setMonthOpen(o => !o)}
                    >
                      <span>{monthOptions.find(o => o.value === selectedMonth)?.short || selectedMonth}</span>
                      <FiChevronDown size={12} className={`cd-chevron ${monthOpen ? 'cd-chevron--open' : ''}`} />
                    </button>
                    {monthOpen && (
                      <div className="cd-month-panel">
                        {monthOptions.map(o => (
                          <button
                            key={o.value}
                            className={`cd-company-item ${o.value === selectedMonth ? 'cd-company-item--active' : ''}`}
                            onClick={() => { setSelectedMonth(o.value); setMonthOpen(false); }}
                          >
                            <span>{o.label}</span>
                            {o.value === selectedMonth && <FiCheck size={13} className="cd-company-check" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {(from || to) && (
                  <span style={{ fontSize: 11, color: '#888' }}>
                    {fmtDate(from)} → {fmtDate(to)}
                  </span>
                )}
              </div>

              {daily.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: 13 }}>
                  No data for {from || to ? 'selected range' : 'this month'}
                </div>
              ) : (() => {
                const trendData = daily.map(d => {
                  // d.date is always "YYYY-MM-DD" from the backend
                  const dt = new Date(d.date + 'T00:00:00');
                  return {
                    ...d,
                    dateLabel: dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    fullDate:  dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
                  };
                });
                const totalWon   = trendData.reduce((s, d) => s + d.won, 0);
                const totalAll   = trendData.reduce((s, d) => s + d.total, 0);
                const winPct     = totalAll > 0 ? Math.round((totalWon / totalAll) * 100) : 0;
                return (
                  <>
                    <div className="cd-trend-meta">
                      <span className="cd-trend-rate">{totalWon}</span>
                      <div>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Total won this month</div>
                        <span className={`cd-trend-badge ${winPct >= 50 ? 'cd-trend-badge--up' : 'cd-trend-badge--down'}`}>
                          {winPct}% win rate
                        </span>
                      </div>
                    </div>

                    <div className="cd-trend-chart">
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={trendData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis
                            dataKey="dateLabel"
                            tick={{ fontSize: 11, fill: '#aaa' }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                            minTickGap={40}
                            tickMargin={15}
                            height={40}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#aaa' }}
                            axisLine={false}
                            tickLine={false}
                            width={30}
                            allowDecimals={false}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0].payload;
                              return (
                                <div className="cd-trend-tooltip">
                                  <div className="cd-trend-tt-day" style={{ marginBottom: 6 }}>{d.fullDate}</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#2563eb', display: 'inline-block', flexShrink: 0 }} />
                                    <span style={{ fontSize: 13, color: '#333' }}>Won inspections</span>
                                    <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 14, color: '#1a2a4d' }}>{d.won}</span>
                                  </div>
                                </div>
                              );
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="won"
                            stroke="#2563eb"
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: '#fff', stroke: '#2563eb', strokeWidth: 2 }}
                            activeDot={{ r: 6, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                );
              })()}
            </div>

          </div>

          {/* ── Bar Chart ── */}
          {/* <div className="cd-section">
            <div className="cd-section-header">
              <div className="cd-section-title">
                ENQUIRIES BY COORDINATOR
                {selectedCoord && (
                  <span className="cd-chart-filter-tag">
                    {selectedCoord.name}
                    <button onClick={() => setSelectedCoord(null)} className="cd-chart-filter-clear">
                      <FiX size={11} />
                    </button>
                  </span>
                )}
              </div>
            </div>
            <div className="cd-chart-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 8, right: 20, left: -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis width={35} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                  />
                  <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="In process" stackId="a" fill="#ffc107" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Won"        stackId="a" fill="#28a745" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Lost"       stackId="a" fill="#dc3545" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div> */}

          {/* ── Revenue Total + Revenue by Clients (50 / 50) ── */}
          <div className="cd-two-col">

            {/* ── Revenue Total ── */}
            <div className="cd-section cd-trend-card">
              <div className="cd-trend-header">
                <div>
                  <div className="cd-section-title" style={{ marginBottom: 2 }}>REVENUE TOTAL (CLIENT)</div>
                  <div className="cd-trend-sub">On Basis of Enquiry</div>
                </div>
                {!from && !to && <span style={{ fontSize: 11, color: '#888' }}>{monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}</span>}
                {(from || to) && <span style={{ fontSize: 11, color: '#888' }}>{fmtDate(from)} → {fmtDate(to)}</span>}
              </div>

              <div className="cd-trend-meta">
                <span className="cd-trend-rate">₹{offerPriceTrend.reduce((s, d) => s + d.revenueINR, 0).toLocaleString('en-IN')}</span>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                  Total revenue {from || to ? 'for selected range' : `for ${monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}`}
                </div>
              </div>

              {offerPriceTrend.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: 13 }}>No data</div>
              ) : (
                <div className="cd-trend-chart">
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={offerPriceTrend} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={40} tickMargin={12} height={36} />
                      <YAxis tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false} width={48}
                        tickFormatter={v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`} />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="cd-trend-tooltip">
                            <div className="cd-trend-tt-day" style={{ marginBottom: 6 }}>{d.fullDate}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#2563eb', display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ fontSize: 13, color: '#333' }}>Revenue</span>
                              <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 14, color: '#1a2a4d' }}>₹{d.revenueINR.toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                        );
                      }} />
                      <Line type="linear" dataKey="revenueINR" name="Revenue (INR)" stroke="#2563eb" strokeWidth={2.5}
                        dot={{ r: 3, fill: '#fff', stroke: '#2563eb', strokeWidth: 2 }}
                        activeDot={{ r: 5, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* ── Revenue by Clients (top 10 horizontal bar) ── */}
            <div className="cd-section">
              <div className="cd-trend-header" style={{ marginBottom: 12 }}>
                <div>
                  <div className="cd-section-title" style={{ marginBottom: 2 }}>REVENUE BY TOP CLIENTS</div>
                  <div className="cd-trend-sub">
                    Top 10 clients · {from || to ? `${fmtDate(from)} → ${fmtDate(to)}` : monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}
                  </div>
                </div>
              </div>

              {topClients.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: 13 }}>No client data</div>
              ) : (() => {
                const visClients = showAllClients ? topClients : topClients.slice(0, 10);
                return (
                  <>
                    <div style={showAllClients ? { maxHeight: 340, overflowY: 'auto' } : {}}>
                      <ResponsiveContainer width="100%" height={visClients.length * 32 + 24}>
                        <BarChart layout="vertical" data={visClients} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                          <XAxis type="number" tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false}
                            tickFormatter={v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`} />
                          <YAxis type="category" dataKey="name" width={105} tick={{ fontSize: 11, fill: '#444' }} axisLine={false} tickLine={false}
                            tickFormatter={v => v.length > 15 ? v.slice(0, 15) + '…' : v} />
                          <Tooltip cursor={false} formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                          <Bar dataKey="revenueINR" name="Revenue" fill="#2563eb" radius={[0, 4, 4, 0]} barSize={14} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {topClients.length > 10 && (
                      <button onClick={() => setShowAllClients(v => !v)} style={{ marginTop: 8, fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontWeight: 600 }}>
                        {showAllClients ? 'Show less ▲' : `View more (${topClients.length - 10} more) ▼`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

          </div>

          {/* ── Gross Profit (full width) ── */}
          <div className="cd-section cd-trend-card">
            <div className="cd-trend-header">
              <div>
                <div className="cd-section-title" style={{ marginBottom: 2 }}>GROSS PROFIT</div>
                <div className="cd-trend-sub">On Basis of Enquiry (INR)</div>
              </div>
              {(from || to) && <span style={{ fontSize: 11, color: '#888' }}>{fmtDate(from)} → {fmtDate(to)}</span>}
              {!from && !to && <span style={{ fontSize: 11, color: '#888' }}>{monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}</span>}
            </div>

            <div className="cd-trend-meta">
              <span className="cd-trend-rate">₹{offerPriceTrend.reduce((s, d) => s + d.grossProfitINR, 0).toLocaleString('en-IN')}</span>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                Total gross profit {from || to ? 'for selected range' : `for ${monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}`}
              </div>
            </div>

            {offerPriceTrend.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: 13 }}>No gross profit data</div>
            ) : (
              <div className="cd-trend-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={offerPriceTrend} margin={{ top: 8, right: 16, left: 16, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={40} tickMargin={15} height={40} />
                    <YAxis tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} width={52}
                      tickFormatter={v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="cd-trend-tooltip">
                          <div className="cd-trend-tt-day" style={{ marginBottom: 6 }}>{d.fullDate}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a', display: 'inline-block', flexShrink: 0 }} />
                            <span style={{ fontSize: 13, color: '#333' }}>Gross Profit</span>
                            <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 14, color: '#1a2a4d' }}>₹{d.grossProfitINR.toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      );
                    }} />
                    <Line type="linear" dataKey="grossProfitINR" name="Gross Profit (INR)" stroke="#16a34a" strokeWidth={2.5}
                      dot={{ r: 4, fill: '#fff', stroke: '#16a34a', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#16a34a', stroke: '#fff', strokeWidth: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── Cost by Inspector + Cost by Country (50 / 50) ── */}
          <div className="cd-two-col">

            {/* Left column: TOTAL INSPECTOR COST + REVENUE BY VENDOR COUNTRY stacked */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Cost by Inspector — daily total cost line chart */}
            <div className="cd-section cd-trend-card">
              <div className="cd-trend-header">
                <div>
                  <div className="cd-section-title" style={{ marginBottom: 2 }}>TOTAL INSPECTOR COST</div>
                  <div className="cd-trend-sub">
                    On Basis of Enquiry · {from || to ? `${fmtDate(from)} → ${fmtDate(to)}` : monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}
                  </div>
                </div>
              </div>

              <div className="cd-trend-meta">
                <span className="cd-trend-rate">₹{offerPriceTrend.reduce((s, d) => s + d.costINR, 0).toLocaleString('en-IN')}</span>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
                  Total inspector cost {from || to ? 'for selected range' : `for ${monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}`}
                </div>
              </div>

              {offerPriceTrend.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: 13 }}>No inspector cost data</div>
              ) : (
                <div className="cd-trend-chart">
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={offerPriceTrend} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={40} tickMargin={12} height={36} />
                      <YAxis tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false} width={48}
                        tickFormatter={v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`} />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="cd-trend-tooltip">
                            <div className="cd-trend-tt-day" style={{ marginBottom: 6 }}>{d.fullDate}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ fontSize: 13, color: '#333' }}>Cost</span>
                              <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 14, color: '#1a2a4d' }}>₹{d.costINR.toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                        );
                      }} />
                      <Line type="linear" dataKey="costINR" name="Cost (INR)" stroke="#f59e0b" strokeWidth={2.5}
                        dot={{ r: 3, fill: '#fff', stroke: '#f59e0b', strokeWidth: 2 }}
                        activeDot={{ r: 5, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Revenue by Vendor Country */}
            <div className="cd-section">
              <div className="cd-trend-header" style={{ marginBottom: 12 }}>
                <div>
                  <div className="cd-section-title" style={{ marginBottom: 2 }}>REVENUE BY VENDOR COUNTRY</div>
                  <div className="cd-trend-sub">
                    On Basis of Enquiry · {from || to ? `${fmtDate(from)} → ${fmtDate(to)}` : monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}
                  </div>
                </div>
              </div>
              {revenueByCountry.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: 13 }}>No vendor country data</div>
              ) : (() => {
                const visRevCountries = showAllRevCountries ? revenueByCountry : revenueByCountry.slice(0, 10);
                return (
                  <>
                    <div style={showAllRevCountries ? { maxHeight: 340, overflowY: 'auto' } : {}}>
                      <ResponsiveContainer width="100%" height={visRevCountries.length * 32 + 24}>
                        <BarChart layout="vertical" data={visRevCountries} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                          <XAxis type="number" tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false}
                            tickFormatter={v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`} />
                          <YAxis type="category" dataKey="name" width={105} tick={{ fontSize: 11, fill: '#444' }} axisLine={false} tickLine={false}
                            tickFormatter={v => v.length > 15 ? v.slice(0, 15) + '…' : v} />
                          <Tooltip cursor={false} formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue (INR)']} />
                          <Bar dataKey="revenueINR" name="Revenue (INR)" fill="#7c3aed" radius={[0, 4, 4, 0]} barSize={14} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {revenueByCountry.length > 10 && (
                      <button onClick={() => setShowAllRevCountries(v => !v)} style={{ marginTop: 8, fontSize: 11, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontWeight: 600 }}>
                        {showAllRevCountries ? 'Show less ▲' : `View more (${revenueByCountry.length - 10} more) ▼`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            </div> {/* end left stacked column */}

            {/* Cost by Country — right column */}
            <div className="cd-section">
              <div className="cd-trend-header" style={{ marginBottom: 12 }}>
                <div>
                  <div className="cd-section-title" style={{ marginBottom: 2 }}>INSPECTOR COST BY COUNTRY</div>
                  <div className="cd-trend-sub">
                    On Basis of Enquiry · {from || to ? `${fmtDate(from)} → ${fmtDate(to)}` : monthOptions.find(o => o.value === offerMonth)?.short || offerMonth}
                  </div>
                </div>
              </div>
              {costByCountry.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '32px 0', fontSize: 13 }}>No country cost data</div>
              ) : (() => {
                const visCountries = showAllCountries ? costByCountry : costByCountry.slice(0, 10);
                return (
                  <>
                    <div style={showAllCountries ? { maxHeight: 340, overflowY: 'auto' } : {}}>
                      <ResponsiveContainer width="100%" height={visCountries.length * 32 + 24}>
                        <BarChart layout="vertical" data={visCountries} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                          <XAxis type="number" tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false}
                            tickFormatter={v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`} />
                          <YAxis type="category" dataKey="name" width={105} tick={{ fontSize: 11, fill: '#444' }} axisLine={false} tickLine={false}
                            tickFormatter={v => v.length > 15 ? v.slice(0, 15) + '…' : v} />
                          <Tooltip cursor={false} formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Cost (INR)']} />
                          <Bar dataKey="costINR" name="Cost (INR)" fill="#dc3545" radius={[0, 4, 4, 0]} barSize={14} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {costByCountry.length > 10 && (
                      <button onClick={() => setShowAllCountries(v => !v)} style={{ marginTop: 8, fontSize: 11, color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontWeight: 600 }}>
                        {showAllCountries ? 'Show less ▲' : `View more (${costByCountry.length - 10} more) ▼`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default CoordinatorDashboard;
